import { CONTACT_DRAWER_QUERY_KEYS } from '@/components/contact/contact-drawer-url-state';
import { CloseButton } from '@/components/ui/close-button';
import { SelectedAvatarsCluster } from '@/components/ui/selected-avatars-cluster';
import useAffiliateBulkEmailStore, {
  type RecipientData,
} from '@/stores/affiliate-bulk-email-store';
import { ActionBar, Button, Portal, Text } from '@chakra-ui/react';
import { DownloadDataWindow, Mail } from 'iconoir-react';
import { useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface AffiliateBulkActionBarProps {
  affiliates: Pick<RecipientData, 'id' | 'name' | 'avatarUrl'>[];
  onSendEmail: () => void;
  onExport: () => void;
}

const AffiliateBulkActionBar: React.FC<AffiliateBulkActionBarProps> = ({
  affiliates,
  onExport,
  onSendEmail,
}) => {
  const { t } = useTranslation();
  const selectedIds = useAffiliateBulkEmailStore((s) => s.selectedIds);
  const selectAllMatching = useAffiliateBulkEmailStore((s) => s.selectAllMatching);
  const matchingTotal = useAffiliateBulkEmailStore((s) => s.matchingTotal);
  const clearSelection = useAffiliateBulkEmailStore((s) => s.clearSelection);

  // The unified contact drawer reuses this same bulk-email store to seed a single
  // recipient for its outreach composer, which would otherwise light up this
  // table-level bar. Suppress it while a contact drawer overlay is open.
  const [contactId] = useQueryState(CONTACT_DRAWER_QUERY_KEYS.contactId);

  const count = selectAllMatching ? matchingTotal : selectedIds.size;
  const isOpen = count > 0 && !contactId;
  const selected = useMemo(
    () => (selectAllMatching ? affiliates : affiliates.filter((a) => selectedIds.has(a.id))),
    [affiliates, selectedIds, selectAllMatching]
  );

  return (
    <ActionBar.Root open={isOpen}>
      <Portal>
        <ActionBar.Positioner zIndex="modal">
          <ActionBar.Content>
            <SelectedAvatarsCluster people={selected} totalCount={count} />
            <Text fontSize="13px" color="whiteAlpha.900" display="inline-flex" gap={1}>
              {t('bulkEmail.actionBar.selected', { count })}
            </Text>
            <ActionBar.Separator mx={1} />
            <Button
              variant="plain"
              color="whiteAlpha.800"
              size="xs"
              h={8}
              minW={8}
              px={3}
              rounded="md"
              onClick={onExport}
              _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
              _active={{ bg: 'whiteAlpha.300' }}
            >
              <DownloadDataWindow width={16} height={16} />
              {t('affiliates.export.action')}
            </Button>
            <Button
              variant="subtle"
              size="xs"
              h={8}
              minW={8}
              px={3}
              rounded="md"
              onClick={onSendEmail}
              data-action-bar-primary
            >
              <Mail width={16} height={16} />
              {t('bulkEmail.actionBar.sendEmail')}
            </Button>
            <ActionBar.Separator ml={1} />
            <CloseButton
              size="xs"
              h={8}
              minW={8}
              borderColor="whiteAlpha.300"
              color="whiteAlpha.800"
              aria-label={t('common.close')}
              onClick={clearSelection}
              _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
              _active={{ bg: 'whiteAlpha.300' }}
            />
          </ActionBar.Content>
        </ActionBar.Positioner>
      </Portal>
    </ActionBar.Root>
  );
};

export default AffiliateBulkActionBar;
